import { Space, Spinner } from '@sravni/react-design-system';
import { Text } from '@sravni/react-design-system/lib/Typography';
import { useIsMobile } from '@sravni/react-utils';
import dynamic from 'next/dynamic';
import { useCallback, useMemo, useState } from 'react';

import { SELECTION_PAGE } from 'constants/webRoutes';

import { sendEventRatesChartSelectItem, sendEventRatesChartViewItem } from 'shared/lib/sendGAEvents';
import { AdaptiveCard } from 'shared/ui/AdaptiveCard';

import { useGetLoanChartFiltersQuery } from 'entities/chartFilters';
import type { ILoansData } from 'entities/loansData';
import { useGetLoansDataMutation } from 'entities/loansData';

import { BASE_URL } from '../../../../../constants/routes';
import { RatesControls } from '../RatesControls/RatesControls';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const BarChart = dynamic(() => import('@sravni/creditselection-react/lib/Charts').then((m) => m.BarChart), {
  ssr: false,
});

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const MobileBarChart = dynamic(() => import('@sravni/creditselection-react/lib/Charts').then((m) => m.MobileBarChart), {
  ssr: false,
});

export const RatesChart = () => {
  const isMobile = useIsMobile();
  const [chartData, setChartData] = useState<
    Array<{
      id: string;
      icon: string;
      rate: number;
      name: string;
      href: string;
    }>
  >([]);

  const [loansData, setLoansData] = useState<ILoansData[]>([]);
  const [analyticsData, setAnalyticsData] = useState<{ type: string; sum: string; term: string; month: string } | null>(
    null,
  );

  const [months, setMonths] = useState<string[]>([]);
  const [getLoansData] = useGetLoansDataMutation();
  const { data: filtersData } = useGetLoanChartFiltersQuery();
  const createChartData = (loans: ILoansData['banks']) =>
    loans?.map((item) => ({
      id: item.bankId,
      icon: item.iconSrc,
      rate: item.rate,
      name: item.name,
      href: `${BASE_URL}${SELECTION_PAGE}?skipLanding=true`,
    }));

  const setData = async ({ type, sum, term, month }: { type: string; sum: string; term: string; month?: string }) => {
    const loans = await getLoansData({ type, sum, term }).unwrap();

    setLoansData(loans);

    const uniqueMonths = Array.from(new Set(loans.map((item) => item.monthISO))).reverse();
    setAnalyticsData({ type, sum, term, month: uniqueMonths[0] });
    setMonths(uniqueMonths);

    let currentLoans = loans.find((item) => item.monthISO === month);

    if (!currentLoans) {
      currentLoans = loans[loans.length - 1] || {};
    }

    setChartData(createChartData(currentLoans.banks));
  };

  const changeMonth = (month: string) => {
    const loansByData = loansData.find((item) => item.monthISO === month)?.banks;
    setAnalyticsData((data) => (data ? { ...data, month } : null));
    if (loansByData?.length) {
      const chartItems = createChartData(loansByData);
      setChartData(chartItems);
    }
  };

  const convertAnalyticsData = useCallback(
    (values: { type: string; sum: string; term: string; month: string }) => {
      const type = filtersData?.find((item) => item.value === values.type);
      const typeLabel = type?.label || '';

      const sum = type?.amounts.find((item) => item.value === values.sum);
      const sumLabel = sum?.label || '';

      const term = sum?.terms.find((item) => item.value === values.term);
      const termLabel = term?.label || '';

      const month = new Date(values.month).toLocaleString('default', { month: 'long', year: 'numeric' });

      return { type: typeLabel, sum: sumLabel, term: termLabel, month };
    },
    [filtersData],
  );

  const chart = useMemo(() => {
    if (!chartData.length || !analyticsData) return <Spinner />;
    const key = Math.random();
    if (isMobile)
      return (
        // @ts-expect-error('BarChart' импортится динамически из-за ошибки https://github.com/plouc/nivo/issues/2310)
        <MobileBarChart
          key={key}
          // @ts-ignore
          data={chartData}
          onItemInteract={(id: string, name: string, rate: string) => {
            sendEventRatesChartViewItem([id, name, rate].join('|'));
          }}
          onItemSelect={(id: string, name: string, rate: string) => {
            sendEventRatesChartSelectItem([id, name, rate].join('|'), convertAnalyticsData(analyticsData));
          }}
        />
      );

    return (
      // @ts-expect-error('BarChart' импортится динамически из-за ошибки https://github.com/plouc/nivo/issues/2310)
      <BarChart
        key={key}
        // @ts-ignore
        data={chartData}
        onItemInteract={(id: string, name: string, rate: string) => {
          sendEventRatesChartViewItem([id, name, rate].join('|'));
        }}
        onItemSelect={(id: string, name: string, rate: string) => {
          sendEventRatesChartSelectItem([id, name, rate].join('|'), convertAnalyticsData(analyticsData));
        }}
      />
    );
  }, [chartData, analyticsData, isMobile, convertAnalyticsData]);

  return (
    <AdaptiveCard
      desktopSize={24}
      color="light"
    >
      <Space
        direction={isMobile ? 'column-reverse' : 'vertical'}
        size={isMobile ? 16 : 20}
      >
        <RatesControls
          setData={setData}
          changeMonth={changeMonth}
          months={months}
        />
        {chart}
        <Text
          className="h-color-D30"
          size={12}
        >{`Данные обновлены: ${
          months.length
            ? new Date(Math.max(...months.map((month) => new Date(month).getTime()))).toLocaleDateString()
            : 'Нет данных'
        }`}</Text>
      </Space>
    </AdaptiveCard>
  );
};
